import { useMemo } from 'react';

import { trpc } from '@/api/tRpc';
import { STALE_SHORT } from '@/lib/query-options';

/**
 * Hook to fetch the adapter configurations of the current retailer and environment.
 *
 * The list includes disabled configurations, so a mapping that points at one still shows its code.
 * Each item carries `id`, `code`, `description` and `adapterType`.
 *
 * @returns { object } Returns tRPC query result with the adapter configurations, ordered by code.
 */
export const useAdapterConfigs = () => trpc.adapterConfigs.list.useQuery(undefined, { staleTime: STALE_SHORT });

/**
 * Hook that maps each adapter configuration id of the current retailer and environment to its code.
 *
 * @returns { object } Returns the code lookup, empty while the list loads, plus the query's loading and error flags and refetch.
 */
export const useAdapterConfigCodes = () => {
  const { data, isError, refetch, isPending } = useAdapterConfigs();
  const codeById = useMemo(
    () => new Map((data ?? []).map((adapterConfig) => [adapterConfig.id, adapterConfig.code])),
    [data],
  );

  return { isError, refetch, codeById, isPending };
};
